import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import ApiServices from "../../../../services/ApiServices";
import {
  CATALOG_ASSOCIATED_CONTENT,
  BUNDLE_ASSOCIATED_CONTENT,
} from "../../../../constants/api-constants";
import { useLoading } from "../../../../hooks/useLoader";
import DataTable from "../../../Tables/DataTable/DataTable";
import { COLUMN_TYPES } from "../../../../constants/common";

/* Same query shape used by the Catalog/Bundle ListContent components — the
 * associated-content endpoints expect a JSON-encoded query as the third URL
 * segment. IgnorePaging=true matches ListContent and returns the full set. */
const DEFAULT_QUERY = {
  searchText: "",
  SortType: "desc",
  IgnorePaging: true,
  SortColumn: "userId",
  PageNumber: 1,
  PageSize: 10,
  filter: {},
};

const ENDPOINT_BY_TYPE = {
  catalog: CATALOG_ASSOCIATED_CONTENT,
  bundle: BUNDLE_ASSOCIATED_CONTENT,
};

/* Read-only column set — field names match the records returned by the
 * associated-content endpoints (mirrors ListOfContentCatalogDataColumns). */
const BASE_COLUMNS = [
  {
    fieldName: "formNumber",
    label: "Form Number",
    isSelected: true,
    type: COLUMN_TYPES.BASIC,
    sort: true,
    width: 136,
  },
  {
    fieldName: "name",
    label: "Content Name",
    isSelected: true,
    type: COLUMN_TYPES.BASIC,
    sort: true,
    width: 220,
  },
  {
    fieldName: "latestVersion",
    label: "Edition Date",
    isSelected: true,
    type: COLUMN_TYPES.BASIC,
    sort: true,
    width: 120,
  },
];

const TYPE_OF_CONTENT_COLUMN = {
  fieldName: "typeOfContentText",
  label: "Type of Content",
  isSelected: true,
  type: COLUMN_TYPES.BASIC,
  sort: true,
  width: 160,
};

const DOCUMENT_TYPE_COLUMN = {
  fieldName: "documentType",
  label: "Types – Content / Catalog",
  isSelected: true,
  type: COLUMN_TYPES.DOCTYPE,
  sort: true,
  width: 218,
};

const TRAILING_COLUMNS = [
  {
    fieldName: "lineOfBusinessList",
    label: "LOB",
    isSelected: true,
    type: COLUMN_TYPES.MULTI_VALUE,
    width: 160,
  },
  {
    fieldName: "countryName",
    label: "Country",
    isSelected: true,
    type: COLUMN_TYPES.BASIC,
    sort: true,
    width: 120,
  },
];

/**
 * ViewItemsDialog
 * Read-only nested popup that lists the content items inside a Catalog or
 * Bundle. Fetches the same associated-content endpoint used by
 * Distributors/Catalog/ListContent and Distributors/Bundle/ListContent:
 *   `${CATALOG_ASSOCIATED_CONTENT | BUNDLE_ASSOCIATED_CONTENT}/${distId}/${id}/${queryJson}`
 *
 * Selections cannot be made from here — see SelectContentDialog for the
 * parent selection flow.
 */
const ViewItemsDialog = ({ open, type, item, distributorId, onClose }) => {
  const { showLoader } = useLoading();
  const [records, setRecords] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortState, setSortState] = useState({
    SortColumn: DEFAULT_QUERY.SortColumn,
    SortType: DEFAULT_QUERY.SortType,
  });
  /* 1s debounce on the search input — matches the SelectContentDialog
   * behaviour and avoids a request per keystroke. */
  const searchDebounceRef = useRef(null);

  const heading = type === "catalog" ? "Catalog Contents" : "Bundle Contents";

  // Bundle view shows the documentType icon column ("Types – Content / Catalog"),
  // catalog view keeps the plain "Type of Content" text column.
  const columns = useMemo(
    () => [
      ...BASE_COLUMNS,
      type === "bundle" ? DOCUMENT_TYPE_COLUMN : TYPE_OF_CONTENT_COLUMN,
      ...TRAILING_COLUMNS,
    ],
    [type],
  );

  // The endpoint returns the full set (IgnorePaging=true), so sort client-side
  // by the active SortColumn / SortType. Strings compared case-insensitively,
  // dates parsed when the column holds an ISO timestamp; numbers compared
  // numerically; nulls/undefineds sink to the bottom.
  const sortedRecords = useMemo(() => {
    const { SortColumn, SortType } = sortState;
    if (!SortColumn || records.length === 0) return records;
    const direction = SortType === "asc" ? 1 : -1;
    const compare = (a, b) => {
      const av = a?.[SortColumn];
      const bv = b?.[SortColumn];
      const aMissing = av === null || av === undefined || av === "";
      const bMissing = bv === null || bv === undefined || bv === "";
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * direction;
      }
      const aDate = Date.parse(av);
      const bDate = Date.parse(bv);
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
        return (aDate - bDate) * direction;
      }
      return (
        String(av).localeCompare(String(bv), undefined, {
          sensitivity: "base",
        }) * direction
      );
    };
    return [...records].sort(compare);
  }, [records, sortState]);

  const handleSort = (sortOrder, sortBy) => {
    setSortState((prev) => {
      let nextSortType = "asc";
      if (prev.SortColumn === sortBy) {
        nextSortType = sortOrder === "asc" ? "desc" : "asc";
      }
      return { SortColumn: sortBy, SortType: nextSortType };
    });
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(value);
    }, 1000);
  };

  useEffect(() => {
    if (!open) {
      setRecords([]);
      setSearchText("");
      setSortState({
        SortColumn: DEFAULT_QUERY.SortColumn,
        SortType: DEFAULT_QUERY.SortType,
      });
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      return undefined;
    }
    const endpoint = ENDPOINT_BY_TYPE[type];
    const id = item?.contentId ?? item?.id;
    const distId = Number(distributorId);
    if (!endpoint || !id || !distId || Number.isNaN(distId) || distId <= 0) {
      setRecords([]);
      return undefined;
    }

    let cancelled = false;
    const fetchItems = async () => {
      try {
        showLoader(true);
        const query = { ...DEFAULT_QUERY, searchText };
        const response = await ApiServices.httpGet(
          `${endpoint}/${distId}/${id}/${JSON.stringify(query)}`,
        );
        if (cancelled) return;
        setRecords(response?.result?.records || []);
      } catch (err) {
        if (!cancelled) toast.error("Internal server error");
      } finally {
        if (!cancelled) showLoader(false);
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [open, type, item, distributorId, searchText, showLoader]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="select-content-dialog select-content-dialog--nested"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        className="select-content-dialog__title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>
          {heading}
          {item?.name ? ` — ${item.name}` : ""}
        </span>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="select-content-dialog__content">
        <div className="sc-datatable-wrap">
          <DataTable
            uniqueKey="contentId"
            columns={columns}
            adminTable={sortedRecords}
            query={{ ...DEFAULT_QUERY, ...sortState, searchText }}
            onSort={handleSort}
            onSearch={handleSearch}
            checkboxesNotRequired
            paginationNotRequired
            permissions={[]}
          />
        </div>
      </DialogContent>

      <DialogActions className="select-content-dialog__actions">
        <Button variant="outlined" onClick={onClose} className="sc-btn">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ViewItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(["catalog", "bundle", "content"]),
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  distributorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
};

ViewItemsDialog.defaultProps = {
  type: undefined,
  item: null,
  distributorId: null,
};

export default ViewItemsDialog;
